import type {
  HierarchicalRulesetConfigField,
  HierarchicalRulesetMetadataField,
  ModifiedNumberField,
  NumberField,
  ValueType
} from '@library/types/rulesets';
import { InputIconWrapper, NumberInput } from '@library/components/inputs';
import { classNames } from '@library/utils';
import { toUSD } from '@bo-utils/common';

export interface LineupLimitsInputProps {
  fieldName: HierarchicalRulesetMetadataField | HierarchicalRulesetConfigField;
  inputLabel: string;
  initialValue: NumberField;
  modifiedValue: ModifiedNumberField;
  allowedTypes: ValueType[];
  handleUpdateRulesetField: ({
    fieldName,
    type,
    value
  }: {
    fieldName:
      | HierarchicalRulesetMetadataField
      | HierarchicalRulesetConfigField;
    type?: ValueType;
    value: string | number | boolean;
  }) => void;
  disabled: boolean;
}

export const LineupLimitsInput = ({
  fieldName,
  inputLabel,
  initialValue,
  modifiedValue,
  allowedTypes,
  handleUpdateRulesetField,
  disabled
}: LineupLimitsInputProps) => {
  const inputType = modifiedValue?.type || initialValue.type;
  const currentValue = String(modifiedValue?.value ?? initialValue.value ?? '');

  const specialField = fieldName === 'refund_in_minutes';
  const icon =
    inputType === 'multiplier' ||
    // (Note: in_game_multiplier is still quirky in that it's restricted
    // to literal under the hood but we want to display the % icon for it)
    fieldName === 'in_game_multiplier' ||
    (allowedTypes.length === 1 && allowedTypes[0] === 'multiplier')
      ? '%'
      : '¢';
  const inputIcon = specialField ? '↳' : icon;
  const isIconDisabled =
    ['refund_in_minutes'].includes(fieldName) || allowedTypes.length === 1;

  // Yellow state
  const isDifferentFromParentUnsaved = !!(
    initialValue.ancestor &&
    modifiedValue?.value &&
    !(
      (modifiedValue?.type === 'multiplier' &&
        Number(modifiedValue?.value) === 100) ||
      (modifiedValue?.type === 'literal' &&
        Number(modifiedValue?.value) === Number(initialValue.ancestor?.value))
    )
  );
  const yellowOutline =
    modifiedValue && isDifferentFromParentUnsaved
      ? 'outline-spottieOttie outline-1 outline-offset-0 focus:ring-spottieOttie focus:ring-1'
      : '';

  // Blue state
  const isDifferentFromParentSaved = !!(
    initialValue.ancestor &&
    Object.hasOwn(initialValue, 'value') &&
    !(
      (initialValue?.type === 'multiplier' &&
        Number(initialValue?.value) === 100) ||
      (initialValue?.type === 'literal' &&
        Number(initialValue?.value) === Number(initialValue.ancestor?.value))
    )
  );
  const blueOutline =
    !modifiedValue && isDifferentFromParentSaved
      ? 'outline-msJackson outline-1 outline-offset-0 focus:ring-msJackson focus:ring-1'
      : '';

  let ancestorDetails;
  // handle special-case fields
  if (fieldName === 'in_game_multiplier') {
    ancestorDetails = initialValue.ancestor
      ? `(${initialValue.ancestor.name}: ${initialValue.ancestor.derived}%)`
      : '';
  } else if (fieldName === 'refund_in_minutes') {
    ancestorDetails = initialValue.ancestor
      ? `(${initialValue.ancestor.name}: ${initialValue.ancestor.derived})`
      : '';
    // handle remaining standard fields
  } else if (
    !initialValue.ancestor &&
    currentValue &&
    inputType === 'literal'
  ) {
    ancestorDetails = `= ${toUSD(Number(Number(currentValue) / 100))}`;
  } else if (
    currentValue &&
    inputType === 'multiplier' &&
    initialValue.ancestor
  ) {
    ancestorDetails = `= ${toUSD(Number((Number(initialValue.ancestor.derived) * Number(currentValue)) / 100 / 100))} (${initialValue.ancestor.name}: ${toUSD(Number(initialValue.ancestor.derived) / 100)})`;
  } else if (
    (!currentValue || inputType === 'literal') &&
    initialValue.ancestor
  ) {
    ancestorDetails = `${!currentValue ? `= ${toUSD(Number(Number(initialValue.ancestor.derived) / 100))} ` : `= ${toUSD(Number(Number(currentValue) / 100))} `}(${initialValue.ancestor.name}: ${toUSD(Number(initialValue.ancestor.derived) / 100)})`;
  } else {
    ancestorDetails = '';
  }

  return (
    <div className="flex flex-col text-sm" key={fieldName}>
      {inputLabel}
      <InputIconWrapper
        icon={inputIcon}
        hoverEnabled
        className="flex flex-col gap-y-1"
        iconClassName={classNames(
          'top-[16.5%]',
          isIconDisabled ? '!bg-soFresh-120 !cursor-default' : ''
        )}
        disabled={isIconDisabled}
        onIconClick={() =>
          handleUpdateRulesetField({
            fieldName,
            type: inputType === 'multiplier' ? 'literal' : 'multiplier',
            value: currentValue
          })
        }
      >
        <NumberInput
          className={classNames(
            'font-heebo bg-soFresh-120 text-soClean-100 justify-center rounded-md border-none p-2 pl-10 text-sm font-medium leading-normal tracking-tight',
            yellowOutline,
            blueOutline
          )}
          id="rulesets-monetary-limits"
          value={currentValue}
          onValueChange={(value) =>
            handleUpdateRulesetField({
              fieldName,
              type: modifiedValue?.type || initialValue.type || 'literal',
              value
            })
          }
          disabled={disabled}
        />
        <span className="text-soClean-160 absolute right-4 top-2 text-sm">
          {ancestorDetails}
        </span>
      </InputIconWrapper>
    </div>
  );
};
